import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';

export default function BrandStorySection() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const imageUrl = useSiteImage('home.brandStory.image', 'https://static.readdy.ai/image/16758960e7c0c7cf7b247f911f106b4f/828b0dc0fb1c402bdaee12182d277157.jpeg');

  return (
    <section id="brand-story" className="w-full bg-white py-20 md:py-28 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column */}
          <div className="flex flex-col gap-8">
            {/* Section label */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('home.brandStory.label')}
              </span>
            </div>

            {/* Heading */}
            <div className="flex flex-col gap-1">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-[1.15]">
                {t('home.brandStory.heading')}
              </h2>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light italic text-[#8A8A8A] leading-[1.15]">
                {t('home.brandStory.subheading')}
              </h3>
            </div>

            {/* Body text */}
            <p className="text-sm text-[#8A8A8A] font-light leading-[1.9] max-w-lg">
              {t('home.brandStory.body')}
            </p>

            {/* 3 pillar cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
              {[
                {
                  icon: 'ri-first-aid-kit-line',
                  title: t('home.brandStory.pillar1.title'),
                  subtitle: t('home.brandStory.pillar1.sub'),
                },
                {
                  icon: 'ri-flask-line',
                  title: t('home.brandStory.pillar2.title'),
                  subtitle: t('home.brandStory.pillar2.sub'),
                },
                {
                  icon: 'ri-shield-check-line',
                  title: t('home.brandStory.pillar3.title'),
                  subtitle: t('home.brandStory.pillar3.sub'),
                },
              ].map((pillar) => (
                <div
                  key={pillar.title}
                  className="flex flex-col gap-3 p-5 rounded-xl bg-[#F0EFED]"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white">
                    <i className={`${pillar.icon} text-[#1B3A5C] text-lg`}></i>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-[#2A2A2A]">{pillar.title}</span>
                    <span className="text-[10px] text-[#8A8A8A] font-light">{pillar.subtitle}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to={prefix + '/brand-story'}
              className="mt-2 px-7 py-3 border border-[#2A2A2A]/20 text-[#2A2A2A] text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer w-fit text-center"
            >
              {t('home.brandStory.cta')}
            </Link>
          </div>

          {/* Right column: product image */}
          <div className="relative flex items-center justify-center h-[400px] md:h-[500px] rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A]">
            <img
              src={imageUrl}
              alt="Rx.K Seoul 3-Step Set"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}